import { useState, useEffect } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card } from "./ui/card";
import {
    Lock, EnvelopeSimple, Phone, House, Warning, X, ArrowLeft, ShieldCheck, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Modal dialog letting a signed-in voter update their PIN, email, mobile, or address.
 * Every change is gated behind a "current PIN" challenge.
 * Address changes surface a red warning about eligibility + auto-notify the voting
 * administrator (handled on the backend).
 */
export default function AccountSettingsDialog({ open, onOpenChange, profile, onSaved }) {
    // "menu" shows the four options. otherwise = the field being edited.
    const [screen, setScreen] = useState("menu");
    const [saving, setSaving] = useState(false);

    // Challenge input + field-specific new value
    const [currentPin, setCurrentPin] = useState("");
    const [newValue, setNewValue] = useState("");
    const [confirmPin, setConfirmPin] = useState(""); // pin change only

    // Reset local state whenever the dialog opens or the screen changes.
    useEffect(() => {
        if (!open) return;
        setScreen("menu");
        setCurrentPin("");
        setNewValue("");
        setConfirmPin("");
    }, [open]);

    useEffect(() => {
        setCurrentPin("");
        setNewValue(screen === "email" ? (profile?.email || "")
            : screen === "mobile" ? (profile?.mobile || "")
            : screen === "address" ? (profile?.address || "")
            : "");
        setConfirmPin("");
    }, [screen, profile]);

    if (!profile) return null;

    const submit = async (payload, successMsg) => {
        setSaving(true);
        try {
            await api.put("/voter/profile", { current_pin: currentPin, ...payload });
            toast.success(successMsg);
            onSaved?.();
            setScreen("menu");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Update failed");
        } finally {
            setSaving(false);
        }
    };

    const handlePinChange = async () => {
        if (!currentPin) return toast.error("Enter your current PIN to authorize the change");
        if (!newValue || newValue.length < 4) return toast.error("New PIN must be at least 4 characters");
        if (newValue !== confirmPin) return toast.error("New PIN and confirmation do not match");
        await submit({ new_pin: newValue }, "PIN updated — the voting authority has been notified");
    };
    const handleEmailChange = async () => {
        if (!currentPin) return toast.error("Enter your current PIN to authorize the change");
        await submit({ email: newValue.trim() }, newValue.trim() ? "Email updated" : "Email removed");
    };
    const handleMobileChange = async () => {
        if (!currentPin) return toast.error("Enter your current PIN to authorize the change");
        await submit({ mobile: newValue.trim() }, newValue.trim() ? "Mobile updated" : "Mobile removed");
    };
    const handleAddressChange = async () => {
        if (!currentPin) return toast.error("Enter your current PIN to authorize the change");
        if (!newValue.trim()) return toast.error("Address cannot be empty");
        await submit({ address: newValue.trim() }, "Address updated — the voting authority has been notified");
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="account-settings-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-heading">
                        {screen !== "menu" && (
                            <button
                                type="button"
                                onClick={() => setScreen("menu")}
                                className="p-1 -ml-1 rounded hover:bg-slate-100"
                                aria-label="Back"
                                data-testid="account-settings-back"
                            >
                                <ArrowLeft size={16} />
                            </button>
                        )}
                        <ShieldCheck size={18} weight="duotone" className="text-civic-700" />
                        {screen === "menu" ? "Manage your account" :
                         screen === "pin" ? "Change PIN" :
                         screen === "email" ? "Change email" :
                         screen === "mobile" ? "Change mobile number" :
                         "Change address"}
                    </DialogTitle>
                    <DialogDescription>
                        {screen === "menu"
                            ? "Update your sign-in PIN or your contact info. Every change is authorized with your current PIN."
                            : "Enter the new value and your current PIN to authorize the change."}
                    </DialogDescription>
                </DialogHeader>

                {screen === "menu" && (
                    <div className="space-y-2" data-testid="account-settings-menu">
                        <MenuRow icon={Lock} label="Sign-in PIN" hint="Requires current PIN" onClick={() => setScreen("pin")} testid="menu-pin" />
                        <MenuRow icon={EnvelopeSimple} label="Email" hint={profile.email || "(none on file)"} onClick={() => setScreen("email")} testid="menu-email" />
                        <MenuRow icon={Phone} label="Mobile number" hint={profile.mobile || "(none on file)"} onClick={() => setScreen("mobile")} testid="menu-mobile" />
                        <MenuRow icon={House} label="Home address" hint={profile.address || "(none on file)"} onClick={() => setScreen("address")} testid="menu-address" danger />
                    </div>
                )}

                {screen === "pin" && (
                    <div className="space-y-3">
                        <div>
                            <Label className="overline text-slate-500">Current PIN</Label>
                            <Input type="password" value={currentPin} onChange={(e) => setCurrentPin(e.target.value)} placeholder="•••••" className="font-mono" data-testid="challenge-pin" autoFocus />
                        </div>
                        <div>
                            <Label className="overline text-slate-500">New PIN</Label>
                            <Input type="password" value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="At least 4 characters" className="font-mono" data-testid="new-pin" />
                        </div>
                        <div>
                            <Label className="overline text-slate-500">Confirm new PIN</Label>
                            <Input type="password" value={confirmPin} onChange={(e) => setConfirmPin(e.target.value)} placeholder="Re-enter new PIN" className="font-mono" data-testid="new-pin-confirm" />
                        </div>
                        <NoticeCard tone="info">
                            The voting authority will be notified that your PIN has changed — no action required on your part.
                        </NoticeCard>
                        <SubmitRow disabled={saving} onCancel={() => setScreen("menu")} onSubmit={handlePinChange} label="Update PIN" testid="submit-pin" />
                    </div>
                )}

                {screen === "email" && (
                    <FieldChange
                        icon={EnvelopeSimple}
                        label="New email"
                        placeholder="jane@example.com"
                        type="email"
                        newValue={newValue}
                        setNewValue={setNewValue}
                        currentPin={currentPin}
                        setCurrentPin={setCurrentPin}
                        saving={saving}
                        onCancel={() => setScreen("menu")}
                        onSubmit={handleEmailChange}
                        submitLabel="Update email"
                        testidPrefix="email"
                        allowEmpty
                        emptyHint="Leave blank to remove the email on file."
                    />
                )}
                {screen === "mobile" && (
                    <FieldChange
                        icon={Phone}
                        label="New mobile number"
                        placeholder="(214) 555-0123"
                        type="tel"
                        newValue={newValue}
                        setNewValue={setNewValue}
                        currentPin={currentPin}
                        setCurrentPin={setCurrentPin}
                        saving={saving}
                        onCancel={() => setScreen("menu")}
                        onSubmit={handleMobileChange}
                        submitLabel="Update mobile"
                        testidPrefix="mobile"
                        allowEmpty
                        emptyHint="Leave blank to remove the mobile on file. Numbers are always stored as (xxx) xxx-xxxx."
                    />
                )}
                {screen === "address" && (
                    <div className="space-y-3">
                        <NoticeCard tone="warn" icon={Warning}>
                            <strong className="font-semibold">Heads up</strong> — changing your address may affect your precinct eligibility. The voting authority will be automatically notified of this change along with your contact info.
                        </NoticeCard>
                        <div>
                            <Label className="overline text-slate-500">New address</Label>
                            <Input value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="123 Main St, City, State ZIP" data-testid="new-address" autoFocus />
                            <p className="text-[11px] text-slate-500 mt-1">Physical street address only — P.O. Boxes are not accepted.</p>
                        </div>
                        <div>
                            <Label className="overline text-slate-500">Current PIN</Label>
                            <Input type="password" value={currentPin} onChange={(e) => setCurrentPin(e.target.value)} placeholder="•••••" className="font-mono" data-testid="challenge-pin" />
                        </div>
                        <SubmitRow disabled={saving} onCancel={() => setScreen("menu")} onSubmit={handleAddressChange} label="Update address" testid="submit-address" danger />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function MenuRow({ icon: Icon, label, hint, onClick, testid, danger = false }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-full text-left flex items-center gap-3 rounded-md border px-3 py-2.5 transition-colors ${
                danger ? "border-amber-200 hover:bg-amber-50" : "border-slate-200 hover:bg-slate-50"
            }`}
            data-testid={testid}
        >
            <span className={`h-8 w-8 grid place-items-center rounded-md ${danger ? "bg-amber-100 text-amber-700" : "bg-civic-50 text-civic-700"}`}>
                <Icon size={16} weight="duotone" />
            </span>
            <span className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-slate-900">{label}</div>
                <div className="text-[11px] text-slate-500 truncate">{hint}</div>
            </span>
            <span className="text-slate-400 text-xs">Change →</span>
        </button>
    );
}

function FieldChange({ icon: Icon, label, placeholder, type, newValue, setNewValue, currentPin, setCurrentPin, saving, onCancel, onSubmit, submitLabel, testidPrefix, allowEmpty, emptyHint }) {
    return (
        <div className="space-y-3">
            <div>
                <Label className="overline text-slate-500">{label} {allowEmpty && <span className="text-slate-400 normal-case">(blank = remove)</span>}</Label>
                <div className="relative">
                    <Input type={type} value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder={placeholder} className="pl-9" data-testid={`new-${testidPrefix}`} autoFocus />
                    <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
                {emptyHint && <p className="text-[11px] text-slate-500 mt-1">{emptyHint}</p>}
            </div>
            <div>
                <Label className="overline text-slate-500">Current PIN</Label>
                <Input type="password" value={currentPin} onChange={(e) => setCurrentPin(e.target.value)} placeholder="•••••" className="font-mono" data-testid="challenge-pin" />
            </div>
            <SubmitRow disabled={saving} onCancel={onCancel} onSubmit={onSubmit} label={submitLabel} testid={`submit-${testidPrefix}`} />
        </div>
    );
}

function SubmitRow({ disabled, onCancel, onSubmit, label, testid, danger = false }) {
    return (
        <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onCancel} disabled={disabled} data-testid="cancel">
                <X size={14} className="mr-1.5" /> Cancel
            </Button>
            <Button
                onClick={onSubmit}
                disabled={disabled}
                className={danger ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-civic-600 hover:bg-civic-700 text-white"}
                data-testid={testid}
            >
                <PaperPlaneTilt size={14} weight="bold" className="mr-1.5" />
                {disabled ? "Saving…" : label}
            </Button>
        </div>
    );
}

function NoticeCard({ tone = "info", icon: Icon, children }) {
    const cls = tone === "warn"
        ? "bg-amber-50 border-amber-200 text-amber-900"
        : "bg-civic-50 border-civic-100 text-civic-900";
    return (
        <Card className={`p-3 flex items-start gap-2 text-[12px] leading-snug ${cls}`}>
            {Icon && <Icon size={16} weight="fill" className="mt-0.5 shrink-0" />}
            <div>{children}</div>
        </Card>
    );
}
